import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, ChevronDown, ChevronUp, X, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { useSubscriptionAlerts } from "@/hooks/useSubscriptionAlerts";
import { supabase } from "@/integrations/supabase/client";

const MONTHS_AR = [
  "يناير", "فبراير", "مارس", "أبريل", "مايو", "يونيو",
  "يوليو", "أغسطس", "سبتمبر", "أكتوبر", "نوفمبر", "ديسمبر",
];

interface Props {
  /** route to "monthly status" page used in the CTA */
  linkTo?: string;
}

// Stable per-tab session id for tracing alert lifecycle
const getSessionId = () => {
  try {
    let sid = sessionStorage.getItem("subs-alerts-session-id");
    if (!sid) {
      sid = (crypto as any).randomUUID?.() ?? `s_${Date.now()}_${Math.random().toString(36).slice(2)}`;
      sessionStorage.setItem("subs-alerts-session-id", sid);
    }
    return sid;
  } catch {
    return `s_${Date.now()}`;
  }
};

const logAlertEvent = async (
  action: "shown" | "dismissed" | "cta_clicked" | "toast",
  data: { month: number; year: number; overdue: any[]; missing: any[]; total: number },
  extra?: Record<string, any>,
) => {
  try {
    const { data: auth } = await supabase.auth.getUser();
    const uid = auth.user?.id;
    if (!uid) return;
    await supabase.from("subscription_alert_logs" as any).insert({
      user_id: uid,
      session_id: getSessionId(),
      action,
      month: data.month,
      year: data.year,
      overdue_count: data.overdue.length,
      missing_count: data.missing.length,
      total_count: data.total,
      user_agent: typeof navigator !== "undefined" ? navigator.userAgent : null,
      metadata: {
        overdue_ids: data.overdue.map((o) => o.invoice_id).filter(Boolean),
        missing_nurseries: data.missing.map((m) => m.nursery_id),
        ...(extra ?? {}),
      },
    });
  } catch (e) {
    // swallow — logging must never break UI
    console.warn("alert log failed", e);
  }
};

const SubscriptionAlertsBanner = ({ linkTo = "/accountant/monthly-status" }: Props) => {
  const { data } = useSubscriptionAlerts();
  const [open, setOpen] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const toastedRef = useRef(false);
  const shownLoggedRef = useRef(false);

  const key = data ? `subs-alerts-dismissed-${data.year}-${data.month}` : "";

  useEffect(() => {
    if (!key) return;
    setDismissed(sessionStorage.getItem(key) === "1");
  }, [key]);

  // Log "shown" once per session for this period
  useEffect(() => {
    if (!data || data.total === 0 || dismissed) return;
    if (shownLoggedRef.current) return;
    const shownKey = `subs-alerts-shown-${data.year}-${data.month}`;
    if (sessionStorage.getItem(shownKey) === "1") {
      shownLoggedRef.current = true;
      return;
    }
    shownLoggedRef.current = true;
    sessionStorage.setItem(shownKey, "1");
    logAlertEvent("shown", data);
  }, [data, dismissed]);

  // Fire one toast per session when alerts are detected
  useEffect(() => {
    if (!data || toastedRef.current || dismissed) return;
    if (data.total === 0) return;
    toastedRef.current = true;
    toast.warning(
      `تنبيه: ${data.overdue.length} فاتورة متأخرة و${data.missing.length} حضانة بدون فاتورة هذا الشهر`,
      { duration: 6000 }
    );
    logAlertEvent("toast", data);
  }, [data, dismissed]);

  if (!data || data.total === 0 || dismissed) return null;

  const handleDismiss = () => {
    if (key) sessionStorage.setItem(key, "1");
    setDismissed(true);
    logAlertEvent("dismissed", data, { dismissed_at: new Date().toISOString() });
  };

  const handleCtaClick = () => {
    logAlertEvent("cta_clicked", data, { target: linkTo });
  };

  return (
    <div
      className="rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-800 p-3 mb-4"
      dir="rtl"
    >
      <div className="flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-sm font-semibold text-amber-900 dark:text-amber-200">
              تنبيه فواتير اشتراكات — {MONTHS_AR[data.month - 1]} {data.year}
            </p>
            {data.overdue.length > 0 && (
              <Badge className="bg-red-100 text-red-700 border-red-300">
                {data.overdue.length} متأخرة
              </Badge>
            )}
            {data.missing.length > 0 && (
              <Badge className="bg-gray-100 text-gray-700 border-gray-300">
                {data.missing.length} بدون فاتورة
              </Badge>
            )}
          </div>
          <p className="text-xs text-amber-800/80 dark:text-amber-200/70 mt-1">
            راجع الحضانات التي تأخر سدادها أو لم تُصدر لها فاتورة لهذا الشهر.
          </p>

          <div className="flex flex-wrap items-center gap-2 mt-2">
            <Button asChild size="sm" variant="outline" className="h-7 text-xs gap-1 border-amber-400">
              <Link to={linkTo} onClick={handleCtaClick}>
                <ExternalLink className="w-3 h-3" /> فتح حالة الشهر
              </Link>
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="h-7 text-xs gap-1"
              onClick={() => setOpen((v) => !v)}
            >
              {open ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              {open ? "إخفاء التفاصيل" : "عرض التفاصيل"}
            </Button>
          </div>

          {open && (
            <div className="mt-3 grid md:grid-cols-2 gap-3">
              {data.overdue.length > 0 && (
                <div className="bg-white dark:bg-background rounded border border-red-200 p-2">
                  <p className="text-xs font-semibold text-red-700 mb-1">متأخرة ({data.overdue.length})</p>
                  <ul className="text-xs space-y-0.5 max-h-40 overflow-y-auto">
                    {data.overdue.map((o) => (
                      <li key={o.nursery_id} className="flex justify-between gap-2">
                        <span className="truncate">{o.nursery_name}</span>
                        <span className="text-red-600 flex-shrink-0">
                          {Number(o.amount || 0).toLocaleString("ar-EG")} ج.م
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {data.missing.length > 0 && (
                <div className="bg-white dark:bg-background rounded border border-gray-200 p-2">
                  <p className="text-xs font-semibold text-gray-700 mb-1">بدون فاتورة ({data.missing.length})</p>
                  <ul className="text-xs space-y-0.5 max-h-40 overflow-y-auto">
                    {data.missing.map((m) => (
                      <li key={m.nursery_id} className="truncate">{m.nursery_name}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
        <Button
          size="icon"
          variant="ghost"
          className="h-7 w-7 flex-shrink-0"
          onClick={handleDismiss}
          aria-label="إغلاق"
        >
          <X className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};

export default SubscriptionAlertsBanner;
